import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DailySales from './pages/DailySales';
import DateRangeReport from './pages/DateRangeReport';
import StoreComparison from './pages/StoreComparison';
import Reports from './pages/Reports';
import Locations from './pages/Locations';
import StoresOverview from './pages/StoresOverview';
import Operations from './pages/Operations';
import LaborAnalytics from './pages/LaborAnalytics';
import CustomerAnalytics from './pages/CustomerAnalytics';
import ExecutiveSummary from './pages/ExecutiveSummary';
import UserManagement from './pages/UserManagement';
import ReportRecipients from './pages/ReportRecipients';
import AdvancedAnalytics from './pages/AdvancedAnalytics';
import ETLMonitoring from './pages/ETLMonitoring';
import { luxuryTheme } from './theme/luxuryTheme';

// Create a client with optimized configuration for daily-changing data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,        // 5 minutes default (can override per query)
      gcTime: 10 * 60 * 1000,          // Keep in cache for 10 minutes (renamed from cacheTime in v5)
      refetchOnMount: 'always',        // Always check for fresh data on mount
      refetchOnReconnect: 'always',    // Refetch when reconnecting
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={luxuryTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <SnackbarProvider 
            maxSnack={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            style={{
              fontFamily: luxuryTheme.typography.fontFamily,
            }}
          >
            <CssBaseline />
            <Router>
              <AuthProvider>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute requireDashboardAccess={true}>
                        <Layout>
                          <Routes>
                            <Route path="/" element={<Navigate to="/executive" replace />} />
                            <Route path="/executive" element={<ExecutiveSummary />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/daily-sales" element={<DailySales />} />
                            <Route path="/date-range" element={<DateRangeReport />} />
                            <Route path="/store-comparison" element={<StoreComparison />} />
                            <Route path="/stores" element={<StoresOverview />} />
                            <Route path="/locations" element={<Locations />} />
                            <Route path="/operations" element={<Operations />} />
                            <Route path="/labor" element={<LaborAnalytics />} />
                            <Route path="/customers" element={<CustomerAnalytics />} />
                            <Route path="/analytics" element={<AdvancedAnalytics />} />
                            <Route path="/reports" element={<Reports />} />
                            <Route
                              path="/users"
                              element={
                                <ProtectedRoute allowedRoles={['admin', 'executive']}>
                                  <UserManagement />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/report-recipients"
                              element={
                                <ProtectedRoute allowedRoles={['admin', 'executive']}>
                                  <ReportRecipients />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/etl-monitoring"
                              element={
                                <ProtectedRoute allowedRoles={['admin', 'executive']}>
                                  <ETLMonitoring />
                                </ProtectedRoute>
                              }
                            />
                          </Routes>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </AuthProvider>
            </Router>
            <ReactQueryDevtools initialIsOpen={false} />
          </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;